import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXS } from '@sorare/core/src/atoms/typography';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';

import { RewardBaseIcon } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RewardBase/RewardBaseIcon/index.tsx';
import { RewardBase } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RewardBase/index.tsx';

import { CardReveal } from './CardReveal/index.tsx';
import type { CardRewardWithRewardChoices_so5Reward } from './__generated__/index.graphql.ts';

const Root = styled.div`
  isolation: isolate;
  position: relative;
  transform-style: preserve-3d;
  aspect-ratio: 210 / 340;
`;
const CardImage = styled.img`
  object-fit: cover;
  height: 100%;
`;
const Foreground = styled.div`
  width: 100%;
  height: 100%;

  transform: translateZ(-1px);

  .revealed & {
    transform: rotateY(180deg) translateZ(0px);
    opacity: 0;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;
const Background = styled.div`
  position: absolute;
  height: 100%;
  transform: rotateY(180deg) translateZ(1px);
  opacity: 0;

  .revealed & {
    transform: rotateY(360deg) translateZ(1px);
    opacity: 1;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;

enum CardRevealSteps {
  IDLE,
  REVEALING,
  REVEALED,
}
type Props = {
  so5Reward: CardRewardWithRewardChoices_so5Reward;
  leaguePictureUrl: string;
  onReveal?: () => void;
};
export const CardReward = ({
  so5Reward,
  leaguePictureUrl,
  onReveal,
}: Props) => {
  const { sport } = useSportContext();
  const [step, setStep] = useState(CardRevealSteps.IDLE);
  const { rewards, rewardConfigs } = so5Reward;
  const cardRewardConfig = rewardConfigs.find(isTypeFn('CardRewardConfig'));
  const cardReward = rewards.find(isTypeFn('AnyCardReward'));
  const rewardedCard = cardReward?.card;
  const uppercasedQuality = cardReward?.quality?.toUpperCase() || '';

  return (
    <Root
      className={classNames({ revealed: step === CardRevealSteps.REVEALED })}
    >
      {step === CardRevealSteps.REVEALING && !!rewardedCard && (
        <Modal>
          <CardReveal
            card={rewardedCard}
            onAnimationEnd={() => setStep(CardRevealSteps.REVEALED)}
          />
        </Modal>
      )}
      <Background>
        {!!rewardedCard?.pictureUrl && (
          <CardImage src={rewardedCard.pictureUrl} alt="" />
        )}
      </Background>
      <Foreground>
        <RewardBase
          icon={
            !!cardReward && (
              <Vertical center>
                {!!cardRewardConfig && (
                  <RewardBaseIcon>
                    <RewardConfigImage
                      size={10}
                      anyRewardConfig={cardRewardConfig}
                    />
                  </RewardBaseIcon>
                )}
                <Horizontal gap={0.5} center>
                  {uppercasedQuality === 'TIER_0' ? (
                    <BrandXS>
                      <FormattedMessage
                        id="CardReward.card.quality.star"
                        defaultMessage="Star"
                      />
                    </BrandXS>
                  ) : (
                    <>
                      <BrandXS color="var(--c-nd-600)">
                        <FormattedMessage {...glossary.tier} />
                      </BrandXS>
                      <BrandM>
                        {qualityNames[
                          uppercasedQuality as keyof typeof qualityNames
                        ]
                          .split(' ')
                          .at(1)}
                      </BrandM>
                    </>
                  )}
                </Horizontal>
              </Vertical>
            )
          }
          color={
            rewardedCard ? getRarityColor(rewardedCard.rarityTyped) : undefined
          }
          leaguePictureUrl={
            sport === Sport.FOOTBALL ? leaguePictureUrl : undefined
          }
          onReveal={() => {
            onReveal?.();
            setStep(CardRevealSteps.REVEALING);
          }}
          revealed={step === CardRevealSteps.REVEALED}
        />
      </Foreground>
    </Root>
  );
};

CardReward.fragments = {
  so5Reward: gql`
    fragment CardRewardWithRewardChoices_so5Reward on So5Reward {
      slug
      id
      rewardConfigs {
        id
        ...RewardConfigImage_anyRewardConfigInterface
      }
      rewards {
        id
        ... on AnyCardReward {
          id
          quality
          card {
            slug
            pictureUrl
            rarityTyped
            ...CardReveal_anyCard
          }
        }
      }
    }
    ${CardReveal.fragments.anyCard}
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CardRewardWithRewardChoices_so5Reward>,
};
